import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import 'iconify-icon';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Reusable upload component for admin CMS.
 * - Drag-and-drop or click to browse
 * - Validates extension client-side (server is source of truth)
 * - Shows progress + preview for images
 *
 * Props:
 *   value: current media URL (string) or null
 *   onChange(url, mediaDoc): called with the absolute URL and full media doc
 *   accept: 'image' | 'document' | 'video' | 'all'
 *   folder: subfolder name (e.g. 'blog', 'eneaflix', 'team')
 *   label: optional override for the dropzone text
 *   testId: data-testid root
 */
const FileUploader = ({
  value,
  onChange,
  accept = 'image',
  folder = 'misc',
  label,
  testId = 'file-uploader',
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const acceptMap = {
    image: '.png,.jpg,.jpeg,.webp,.gif,.svg,image/*',
    document: '.pdf,.ppt,.pptx,.doc,.docx',
    video: '.mp4,.webm,.mov,video/*',
    all: 'image/*,video/*,.pdf,.ppt,.pptx,.doc,.docx',
  };

  const dropzoneLabel =
    label ||
    {
      image: 'Arraste uma imagem ou clique (PNG, JPG, WEBP, SVG, GIF — 10MB)',
      document: 'Arraste PDF, PPT ou DOC (até 25MB)',
      video: 'Arraste um vídeo MP4 / WEBM / MOV (até 100MB)',
      all: 'Arraste imagem, vídeo ou documento',
    }[accept];

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', folder);
      const { data } = await axios.post(`${API_URL}/admin/media`, fd, {
        withCredentials: true,
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      const backendBase = process.env.REACT_APP_BACKEND_URL;
      const absoluteUrl = data.url.startsWith('http') ? data.url : `${backendBase}${data.url}`;
      onChange(absoluteUrl, { ...data, url: absoluteUrl });
      toast.success('Arquivo enviado');
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err.response?.data?.detail || 'Falha no upload');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleFiles = (files) => {
    if (!files || !files.length) return;
    handleUpload(files[0]);
  };

  // Treat Base64 data URIs (data:image/...) AND classic image URLs as images.
  // Without this, posters/photos stored as inline Base64 won't preview and the
  // admin sees a generic document icon — leading them to think it's missing.
  const isImage =
    !!value &&
    (value.startsWith('data:image/') ||
      /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(value));

  // For Base64 we don't have a meaningful filename — show a generic label.
  const displayName = value
    ? value.startsWith('data:')
      ? 'Imagem incorporada (Base64)'
      : value.split('/').pop()
    : '';

  return (
    <div data-testid={testId} className="space-y-3">
      <div
        role="button"
        tabIndex={0}
        onClick={() => !uploading && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !uploading) inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!uploading) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          if (!uploading) handleFiles(e.dataTransfer.files);
        }}
        data-testid={`${testId}-dropzone`}
        className={`relative w-full border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-accent-orange bg-accent-orange/5'
            : 'border-neutral-300 hover:border-accent-orange'
        } ${uploading ? 'opacity-70 cursor-wait' : ''}`}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={acceptMap[accept] || acceptMap.all}
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`${testId}-input`}
        />

        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-full bg-neutral-100 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-accent-orange transition-all"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-sm text-neutral-600">Enviando… {progress}%</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <iconify-icon
              icon="solar:upload-linear"
              className="text-3xl text-zinc-700"
            ></iconify-icon>
            <p className="text-sm text-neutral-600">{dropzoneLabel}</p>
          </div>
        )}
      </div>

      {value && (
        <div
          className="flex items-start gap-3 p-3 bg-neutral-50 border border-neutral-200 rounded-lg min-w-0"
          data-testid={`${testId}-preview`}
        >
          {isImage ? (
            <img
              src={value}
              alt="preview"
              className="w-16 h-16 object-cover rounded flex-shrink-0"
            />
          ) : (
            <div className="w-16 h-16 bg-neutral-100 rounded flex items-center justify-center flex-shrink-0">
              <iconify-icon
                icon="solar:document-linear"
                className="text-3xl text-zinc-700"
              ></iconify-icon>
            </div>
          )}
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Arquivo</div>
            {value.startsWith('data:') ? (
              <span
                className="text-xs text-neutral-700 break-words leading-snug block"
                title={displayName}
              >
                Imagem incorporada
              </span>
            ) : (
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-mono break-words leading-snug text-blue-600 hover:underline block truncate"
                title={displayName}
              >
                {displayName}
              </a>
            )}
          </div>
          <button
            type="button"
            onClick={() => onChange('', null)}
            data-testid={`${testId}-clear`}
            className="text-xs text-red-600 hover:text-red-800 flex-shrink-0 self-start"
            aria-label="Remover arquivo"
            title="Remover"
          >
            <iconify-icon icon="solar:trash-bin-linear" className="text-base"></iconify-icon>
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
